import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Button, Input } from '@werkstatt/ui';
import { Link2, Link2Off, Loader2, Search } from 'lucide-react';
import api from '../../lib/api';
import { readApiError } from '../../lib/apiError';
import type { ThreadBooking } from '../../types/mailbox';
import { logger } from '@/utils/logger';

interface Props {
  threadId: number;
  /** Bereits zugeordnete Buchung — dann bietet die Leiste nur das Lösen an. */
  assigned?: ThreadBooking | null;
}

/**
 * Ordnet eine eingegangene Mail von Hand einer Buchung zu.
 *
 * Nötig, wenn der Kunde aus einem anderen Konto schreibt oder sein Mailprogramm
 * die Referenz aus Betreff und Kopfzeilen entfernt hat. Ohne Suchbegriff schlägt
 * der Server die Buchungen derselben Adresse vor — das ist fast immer die gesuchte.
 */
export function AssignBooking({ threadId, assigned }: Props) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => setQuery(search.trim()), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Beim Wechsel des Vorgangs nicht die alte Suche stehen lassen
  useEffect(() => {
    setOpen(false);
    setSearch('');
  }, [threadId]);

  const { data: candidates, isFetching } = useQuery({
    queryKey: ['assign-candidates', threadId, query],
    queryFn: async () => (await api.get<ThreadBooking[]>(
      `/mailbox/threads/${threadId}/assign/candidates`,
      { params: { q: query || undefined } },
    )).data,
    enabled: open,
  });

  const assignMutation = useMutation({
    mutationFn: async (bookingId: number | null) =>
      (await api.post(`/mailbox/threads/${threadId}/assign`, { booking_id: bookingId })).data,
    onSuccess: (result: { message: string }) => {
      toast.success(result.message);
      setOpen(false);
      setSearch('');
      queryClient.invalidateQueries({ queryKey: ['mailbox-thread', threadId] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-threads'] });
      queryClient.invalidateQueries({ queryKey: ['mailbox-stats'] });
    },
    onError: (error: unknown) => {
      logger.error('Assign thread failed', error);
      toast.error(readApiError(error, 'Zuordnung fehlgeschlagen'));
    },
  });

  if (assigned) {
    return (
      <Button
        size="sm"
        variant="ghost"
        onClick={() => assignMutation.mutate(null)}
        disabled={assignMutation.isPending}
      >
        <Link2Off className="mr-1.5 h-4 w-4" aria-hidden />
        Zuordnung lösen
      </Button>
    );
  }

  if (!open) {
    return (
      <Button size="sm" variant="outline" onClick={() => setOpen(true)}>
        <Link2 className="mr-1.5 h-4 w-4" aria-hidden />
        Buchung zuordnen
      </Button>
    );
  }

  return (
    <div className="w-full space-y-2 rounded-md border border-border bg-muted/30 p-3">
      <div className="flex items-center gap-2">
        <div className="relative flex-1">
          <Search
            className="pointer-events-none absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Referenz, Name oder E-Mail"
            className="pl-8"
            aria-label="Buchung suchen"
            autoFocus
          />
        </div>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Abbrechen</Button>
      </div>

      {isFetching ? (
        <div className="flex justify-center py-4 text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
        </div>
      ) : candidates && candidates.length > 0 ? (
        <ul className="max-h-56 space-y-1 overflow-y-auto">
          {candidates.map((item) => (
            <li key={item.id}>
              <button
                type="button"
                onClick={() => assignMutation.mutate(item.id)}
                disabled={assignMutation.isPending}
                className="w-full rounded-md px-2 py-1.5 text-left text-sm hover:bg-muted
                  focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <span className="font-medium">{item.reference}</span>
                <span className="text-muted-foreground">
                  {' · '}{item.local_datetime} · {item.customer_name}
                  {item.vehicle_name ? ` · ${item.vehicle_name}` : ''}
                </span>
              </button>
            </li>
          ))}
        </ul>
      ) : (
        <p className="py-2 text-sm text-muted-foreground">
          {query
            ? 'Keine Buchung gefunden. Versuchen Sie die Referenznummer.'
            : 'Zu dieser Adresse gibt es keine Buchung. Suchen Sie oben nach Name'
              + ' oder Referenz.'}
        </p>
      )}
    </div>
  );
}
